import { ApiTranslation } from '../api-translation';
import { AuthedButton } from '../authed-button';
import { CurrencyView } from '../currency-view';
import { useBalance } from './hooks/use-balance';
import { APP_CHAIN, CTF_ADDRESS, IS_DEBUG } from '@/client/confs/app';
import { useAuth } from '@/client/hooks/use-auth';
import { useSignedAllowance } from '@/client/hooks/use-signed-allowance';
import { useSignedUserInfo } from '@/client/hooks/use-signed-user-info';
import { consumeTransactionSuccess, watchTransaction } from '@/client/hooks/use-transaction-recovery';
import { useWallet } from '@/client/hooks/use-wallet';
import { usePendingPositionStore, wirePendingPositionSse } from '@/client/store/pending-position-store';
import { OptionSide, useTradeParamsStore, useTradeSettingStore } from '@/client/store/trade-store';
import {
  canResumeTransaction,
  failTransactionRequest,
  finishTransactionRequest,
  sendTransaction,
  startTransaction,
  useTransactionStore,
} from '@/client/store/transaction-store';
import { cn, getErrorMessage } from '@/client/utils';
import { api } from '@/client/utils/api';
import { publicClient } from '@/client/utils/chain-client';
import { serverNow, serverNowMs } from '@/client/utils/clock';
import { extractDepositFill, extractWithdrawFill } from '@/client/utils/deposit-fill';
import { handledKeyDownCallBackEnter } from '@/client/utils/handle';
import { signerMatchesSession } from '@/client/utils/signer-guard';
import {
  isQuoteUsable,
  minimumOutput,
  quoteKey,
  transactionDeadline,
  type QuoteIdentity,
} from '@/client/utils/trade-parameters';
import { useEventDetail } from '@/libs/react-sdk/api/use-event-detail';
import { useMarketDetail } from '@/libs/react-sdk/api/use-market-detail';
import { useBuyWin } from '@/libs/react-sdk/contracts/use-buy-win';
import { useOptions } from '@/libs/react-sdk/contracts/use-options';
import { useSellWin } from '@/libs/react-sdk/contracts/use-sell-win';
import { OrderSide } from '@/libs/sdk/api';
import { predictionAbi } from '@/libs/sdk/contracts/abis/prediction';
import useDebounce from 'ahooks/lib/useDebounce';
import confetti from 'canvas-confetti';
import { useEffect, useMemo, useRef, useState, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Address, erc20Abi, maxUint256 } from 'viem';

// Check if mock mode is enabled
const isMockMode = import.meta.env.VITE_ENABLE_MOCK === 'true';

// Smart auto-slippage: compute buffer based on price impact from chain simulation
// Returns buffer percentage in [0.5, 50] range
const computeAutoBuffer = (
  type: 'buy' | 'sell',
  optionPrice: number, // 0 to 1 (probability)
  inputAmount: bigint,
  expectedOutput: bigint,
  decimal: number
): number => {
  const scale = 10 ** decimal;
  const amountNum = Number(inputAmount) / scale;
  const outputNum = Number(expectedOutput) / scale;

  // Base buffer: 0.5% for block-to-block BSC price variance (~3s blocks)
  let buffer = 0.5;

  if (optionPrice > 0 && optionPrice < 1 && amountNum > 0 && outputNum > 0) {
    let priceImpact: number;
    if (type === 'buy') {
      // Buy: base tokens → option tokens; spot rate = 1/price
      const theoreticalOutput = amountNum / optionPrice;
      priceImpact = Math.max(0, (1 - outputNum / theoreticalOutput) * 100);
    } else {
      // Sell: option tokens → base tokens; spot rate = price
      const theoreticalOutput = amountNum * optionPrice;
      priceImpact = Math.max(0, (1 - outputNum / theoreticalOutput) * 100);
    }
    // Buffer = priceImpact * 1.5 + 0.5% base
    buffer = Math.max(0.5, priceImpact * 1.5 + 0.5);
  }

  // Extra buffer for extreme probabilities (AMM curve is steep near 0 or 1)
  if (optionPrice < 0.05 || optionPrice > 0.95) {
    buffer = Math.max(buffer, 3);
  }

  return Math.min(50, Math.max(0.5, buffer));
};

type AcceptedQuote = {
  key: string;
  expectedOutput: bigint;
  minimum: bigint;
};

type TradeConfirmProps = {
  type: 'buy' | 'sell';
  marketAddress: string;
  optionAddress?: string;
  optionSide?: OptionSide;
  amount?: bigint;
  deadlineMinutes: number;
  slippage: number;
  isAutoSlippage: boolean;
  disabled?: boolean;
  uid: string;
  onSuccess?: () => void | Promise<void>;
};
export const TradeConfirm = ({
  type,
  marketAddress,
  optionAddress,
  optionSide,
  amount,
  deadlineMinutes,
  slippage,
  isAutoSlippage,
  disabled,
  uid,
  onSuccess,
}: TradeConfirmProps) => {
  const { t } = useTranslation();
  const [localTrading, setLocalTrading] = useState(false);
  const [tradeStep, setTradeStep] = useState('');
  const [quoteToConfirm, setQuoteToConfirm] = useState<{ key: string; expectedOutput: bigint }>();
  const tradingRef = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const { walletClient } = useWallet();
  const latest = useRef<{ walletClient: typeof walletClient; uid: string; marketAddress: string; quoteKey?: string }>({
    walletClient,
    uid,
    marketAddress,
  });
  latest.current = { walletClient, uid, marketAddress, quoteKey: latest.current.quoteKey };
  const activeTransaction = useTransactionStore(state =>
    walletClient?.account ? state.findActive(APP_CHAIN.id, walletClient.account.address) : undefined
  );
  const resumable =
    activeTransaction &&
    canResumeTransaction(activeTransaction) &&
    activeTransaction.operation === type &&
    activeTransaction.marketAddress.toLowerCase() === marketAddress.toLowerCase();
  const isTrading = localTrading || (!!activeTransaction && !resumable);
  const tradeTxHash = activeTransaction?.hash;
  const bscScanUrl = tradeTxHash ? `https://bscscan.com/tx/${tradeTxHash}` : undefined;
  const currentRequest = (id: string, account: string, expectedQuoteKey?: string) =>
    mounted.current &&
    latest.current.uid === uid &&
    latest.current.marketAddress === marketAddress &&
    latest.current.walletClient?.chain?.id === APP_CHAIN.id &&
    latest.current.walletClient?.account?.address.toLowerCase() === account.toLowerCase() &&
    (!expectedQuoteKey || latest.current.quoteKey === expectedQuoteKey) &&
    (!useTransactionStore.getState().findActive(APP_CHAIN.id, account as Address) ||
      useTransactionStore.getState().findActive(APP_CHAIN.id, account as Address)?.id === id);
  const { isAuthed, logout } = useAuth();
  const signedUserInfo = useSignedUserInfo(api);
  const balance = useBalance({ type, marketAddress, optionAddress, uid });
  const market = useMarketDetail(api, { marketAddress });
  // The traded market's own base token — never the global toggle (mixed
  // multi-base lists mean the toggle can point at a different token)
  const baseToken = useMemo(
    () => ({
      address: market.data?.baseTokenAddress,
      decimal: market.data?.decimal ?? 18,
    }),
    [market.data]
  );
  // Event context for the optimistic portfolio row (same query key as the
  // event page, so this hits warm cache and adds no request in the hot path).
  const eventDetail = useEventDetail(api, { eventId: market.data?.eventId ?? '' });
  const optionsList = useOptions(publicClient, marketAddress as Address);
  const currentOptionInfo = useMemo(
    () => market.data?.optionList.find(item => item.optionAddress.toLowerCase() === optionAddress?.toLowerCase()),
    [market.data?.optionList, optionAddress]
  );

  // Check if this is a multi-option market
  const isMultiOption = (market.data?.optionList.length ?? 0) > 2;

  // Derive option index: prefer on-chain options(), then address suffix (PredictionCTF),
  // then API optionList order as last fallback.
  const currentOptionsIndex = useMemo(() => {
    if (optionsList.data) {
      return optionsList.data.findIndex(item => item.toLowerCase() === optionAddress?.toLowerCase());
    }
    // PredictionCTF: synthetic address suffix encodes the outcome index (e.g. ...0000000 = 0, ...0000001 = 1)
    // Validate against actual option count to prevent wrong-index trades
    if (optionAddress) {
      const suffix = parseInt(optionAddress.slice(-1), 16);
      const maxIndex = market.data?.optionList?.length ?? 2;
      if (!isNaN(suffix) && suffix >= 0 && suffix < maxIndex) return suffix;
    }
    if (optionsList.isError && market.data?.optionList) {
      return market.data.optionList.findIndex(item => item.optionAddress.toLowerCase() === optionAddress?.toLowerCase());
    }
    return -1;
  }, [optionAddress, optionsList.data, optionsList.isError, market.data?.optionList]);

  const price = useMemo(() => currentOptionInfo?.price, [currentOptionInfo?.price]);

  const tradingUnavailable = optionsList.isError && currentOptionsIndex === -1;

  const setAutoSlippageDisplay = useTradeSettingStore(state => state.setAutoSlippageDisplay);

  // Build simulation params (minAmount=0 for simulation — doesn't affect getAmountOut)
  const buySimParams = useMemo(() => {
    if (!marketAddress || !amount || currentOptionsIndex == -1 || !baseToken.address) return undefined;
    let tokenAmount = amount;
    if (balance.data !== undefined) {
      tokenAmount = balance.data - amount < BigInt(Math.pow(10, balance.decimal - 2)) ? balance.data : amount;
    }
    return {
      address: marketAddress as Address,
      optionIndex: BigInt(currentOptionsIndex),
      tokenAmount,
    };
  }, [amount, balance.data, balance.decimal, baseToken, currentOptionsIndex, marketAddress]);

  const sellSimParams = useMemo(() => {
    if (!marketAddress || !amount || !balance.data || currentOptionsIndex === -1 || !currentOptionInfo) return undefined;
    const optionAmount = balance.data - amount < BigInt(Math.pow(10, balance.decimal - 2)) ? balance.data : amount;
    return {
      address: marketAddress as Address,
      optionsLength: optionsList.data?.length ?? market.data?.optionList?.length,
      optionIndex: BigInt(currentOptionsIndex),
      optionAmount,
    };
  }, [
    amount,
    balance,
    currentOptionInfo,
    currentOptionsIndex,
    marketAddress,
    optionsList.data?.length,
    market.data?.optionList?.length,
  ]);

  const buyQuoteRequest = useMemo(() => {
    if (!buySimParams || !optionAddress) return undefined;
    return {
      params: buySimParams,
      identity: {
        chainId: APP_CHAIN.id,
        marketAddress,
        optionAddress,
        side: 'buy' as const,
        amount: buySimParams.tokenAmount,
      },
    };
  }, [buySimParams, marketAddress, optionAddress]);
  const sellQuoteRequest = useMemo(() => {
    if (!sellSimParams || !optionAddress) return undefined;
    return {
      params: sellSimParams,
      identity: {
        chainId: APP_CHAIN.id,
        marketAddress,
        optionAddress,
        side: 'sell' as const,
        amount: sellSimParams.optionAmount,
      },
    };
  }, [marketAddress, optionAddress, sellSimParams]);
  const debounceBuyQuoteRequest = useDebounce(buyQuoteRequest, { wait: 500 });
  const debounceSellQuoteRequest = useDebounce(sellQuoteRequest, { wait: 500 });

  const toBuyWin = useBuyWin(publicClient, debounceBuyQuoteRequest?.params);
  const toSellWin = useSellWin(publicClient, debounceSellQuoteRequest?.params);
  const currentQuoteInput: QuoteIdentity | undefined =
    type === 'buy' ? buyQuoteRequest?.identity : sellQuoteRequest?.identity;
  const quotedInput: QuoteIdentity | undefined =
    type === 'buy' ? debounceBuyQuoteRequest?.identity : debounceSellQuoteRequest?.identity;
  const activeQuote = type === 'buy' ? toBuyWin : toSellWin;
  const quoteUsable = isQuoteUsable(
    { data: activeQuote.data, isError: activeQuote.isError, isFetching: activeQuote.isFetching },
    quotedInput,
    currentQuoteInput
  );
  const currentQuoteKey = currentQuoteInput ? quoteKey(currentQuoteInput) : undefined;
  latest.current.quoteKey = currentQuoteKey;
  const toWinRaw = type == 'buy' ? toBuyWin.data : toSellWin.data;
  const toWin = useMemo(() => Number(toWinRaw), [toWinRaw]);

  const toWinLoading = useMemo(
    () => (type == 'buy' ? toBuyWin.isLoading : toSellWin.isLoading),
    [toBuyWin.isLoading, toSellWin.isLoading, type]
  );

  const autoBuffer = useMemo(() => {
    const expectedOutput = activeQuote.data;
    if (!isAutoSlippage || !quoteUsable || !currentQuoteInput || !price || expectedOutput === undefined) return 0;

    const decimal = baseToken.decimal;
    const optionPrice = Number(price) / Math.pow(10, currentOptionInfo?.decimal ?? decimal);
    return computeAutoBuffer(type, optionPrice, currentQuoteInput.amount, expectedOutput, decimal);
  }, [
    activeQuote.data,
    baseToken.decimal,
    currentOptionInfo?.decimal,
    currentQuoteInput,
    isAutoSlippage,
    price,
    quoteUsable,
    type,
  ]);

  // Sync computed auto-slippage display value to store (side effect, not in useMemo)
  useEffect(() => {
    if (isAutoSlippage) setAutoSlippageDisplay(Math.round(autoBuffer * 10) / 10);
  }, [isAutoSlippage, autoBuffer, setAutoSlippageDisplay]);

  const acceptedSlippageBps = Math.ceil((isAutoSlippage ? autoBuffer : slippage) * 100);
  const settingsValid =
    Number.isInteger(acceptedSlippageBps) &&
    acceptedSlippageBps >= 0 &&
    acceptedSlippageBps < 10000 &&
    Number.isFinite(deadlineMinutes) &&
    deadlineMinutes > 0 &&
    deadlineMinutes <= 1440;
  const quoteNeedsRetry =
    currentQuoteKey !== undefined &&
    quotedInput !== undefined &&
    quoteKey(quotedInput) === currentQuoteKey &&
    !activeQuote.isFetching &&
    (activeQuote.isError || activeQuote.data === undefined || activeQuote.data <= 0n);
  const confirmingChangedQuote = quoteToConfirm?.key === currentQuoteKey;

  useEffect(() => {
    if (quoteToConfirm && quoteToConfirm.key !== currentQuoteKey) setQuoteToConfirm(undefined);
  }, [currentQuoteKey, quoteToConfirm]);

  const signedAllowance = useSignedAllowance(baseToken.address, marketAddress);

  const setAmount = useTradeParamsStore(state => state.setAmount);

  // Helper: detect insufficient gas/funds errors
  const isInsufficientFundsError = (error: any): boolean => {
    const msg = (error?.message || error?.shortMessage || '').toLowerCase();
    return (
      msg.includes('insufficient funds') || msg.includes('insufficient balance') || msg.includes('gas required exceeds')
    );
  };

  // Guards the incident-2026-08-31 class of bug: the wallet extension's
  // currently-selected account (the one that will actually sign the tx) has
  // drifted from the account this session authenticated with. Letting the
  // trade through would sign with an address the backend can't attribute to
  // this uid — the fill lands on-chain but never shows up in the C端 (see
  // promethex-mind/work/incidents/2026-08-31-dpin-silent-unattributed-fills.md).
  // Block and force a fresh SIWE login instead of proceeding silently.
  const guardSignerMatchesSession = (signerAddress: string): boolean => {
    if (signerMatchesSession(signerAddress, signedUserInfo.data?.eoaAddress)) return true;
    toast.error(t('signer-account-mismatch', 'Wallet account changed. Please sign in again to continue trading.'));
    logout();
    return false;
  };

  const acceptCurrentQuote = (): AcceptedQuote | undefined => {
    const expectedOutput = activeQuote.data;
    if (!quoteUsable || expectedOutput === undefined || !currentQuoteKey || !settingsValid) return undefined;
    try {
      return {
        key: currentQuoteKey,
        expectedOutput,
        minimum: minimumOutput(expectedOutput, acceptedSlippageBps),
      };
    } catch {
      return undefined;
    }
  };

  const refreshAcceptedQuote = async (requestId: string, account: Address, accepted: AcceptedQuote) => {
    if (!currentRequest(requestId, account, accepted.key)) return undefined;
    let refreshed: Awaited<ReturnType<typeof activeQuote.refetch>>;
    try {
      refreshed = await activeQuote.refetch({ cancelRefetch: false });
    } catch {
      toast.error(t('quote-unavailable', 'Quote unavailable. Please retry.'));
      return undefined;
    }
    if (!currentRequest(requestId, account, accepted.key)) return undefined;
    if (Number(market.data?.deadline) <= Math.ceil(serverNow())) {
      toast.error(t('market-expired', 'Market has expired'));
      return undefined;
    }
    if (refreshed.isError || refreshed.data === undefined || refreshed.data <= 0n) {
      toast.error(t('quote-unavailable', 'Quote unavailable. Please retry.'));
      return undefined;
    }
    if (refreshed.data < accepted.minimum) {
      setQuoteToConfirm({ key: accepted.key, expectedOutput: refreshed.data });
      toast.error(t('quote-changed-confirm', 'The quote changed. Review and confirm the new minimum.'));
      return undefined;
    }
    setQuoteToConfirm(undefined);
    return {
      minimum: accepted.minimum,
      deadline: transactionDeadline(serverNowMs(), deadlineMinutes),
    };
  };

  const handleBuy = async (requestId: string, accepted: AcceptedQuote) => {
    // In mock mode, use simplified validation and skip contract calls
    if (isMockMode) {
      console.log('[MockTrade] handleBuy called', { type, marketAddress, optionAddress, amount: amount?.toString(), price });
      if (type != 'buy' || !marketAddress || !optionAddress || !buySimParams) {
        console.log('[MockTrade] validation failed - missing required fields');
        return;
      }
      const tradePrice = price ?? '500000';
      try {
        console.log('[MockTrade] calling placeOrder API...');
        await api.market.placeOrder(
          { baseTokenAddress: baseToken.address },
          {
            marketAddress: marketAddress as Address,
            optionAddress: optionAddress as Address,
            side: OrderSide.deposit,
            price: tradePrice,
            amount: buySimParams.tokenAmount.toString(),
            minReceiveAmount: accepted.minimum.toString(),
            deadline: transactionDeadline(serverNowMs(), deadlineMinutes).toString(),
            userOperation: {} as any,
          }
        );
        console.log('[MockTrade] placeOrder success!');
        toast.success(t('buy-success'));
        setAmount(undefined);
        await onSuccess?.();
        return true;
      } catch (error) {
        console.log('[MockTrade] placeOrder error:', error);
        const msg = getErrorMessage(error);
        if (IS_DEBUG) console.log('buy error', error);
        toast.error(msg?.startsWith('transaction-') ? t(msg) : (msg ?? t('buy-failed')));
        return false;
      }
    }

    if (type != 'buy' || !marketAddress || !buySimParams || price === undefined || !baseToken.address) return;
    if (!walletClient?.account) {
      toast.error(t('wallet-not-connected', 'Please connect your wallet'));
      return;
    }
    if (!guardSignerMatchesSession(walletClient.account.address)) return false;
    // Check market deadline before submitting on-chain tx
    if (Number(market.data?.deadline) <= Math.ceil(serverNow())) {
      toast.error(t('market-expired', 'Market has expired'));
      return false;
    }
    const account = walletClient.account;
    const initialAccount = account.address;
    try {
      // Step 1: Check allowance and approve if needed
      const allowance = signedAllowance.data ?? BigInt(0);
      const approvalConfirmed = useTransactionStore.getState().records[requestId]?.approvalConfirmed;
      if (allowance < buySimParams.tokenAmount && !approvalConfirmed) {
        useTransactionStore.getState().update(requestId, { step: 'approve' });
        setTradeStep(t('approving-token', '授權代幣中...'));
        const approveArgs = {
          address: baseToken.address as Address,
          abi: erc20Abi,
          functionName: 'approve' as const,
          args: [marketAddress as Address, maxUint256] as const,
          account,
        };
        const approveGas = await publicClient.estimateContractGas(approveArgs);
        if (!currentRequest(requestId, account.address, accepted.key)) return false;
        const approveTxHash = await sendTransaction(requestId, () =>
          walletClient.writeContract({
            ...approveArgs,
            chain: walletClient.chain,
            gas: (approveGas * 130n) / 100n,
          })
        );
        await watchTransaction(requestId, approveTxHash);
      }

      // Verify wallet account hasn't changed during approve
      if (!currentRequest(requestId, initialAccount, accepted.key)) {
        toast.error(t('wallet-changed', 'Wallet account changed during trade. Please retry.'));
        return false;
      }

      const execution = await refreshAcceptedQuote(requestId, account.address, accepted);
      if (!execution || !currentRequest(requestId, account.address, accepted.key)) return false;

      // Step 2: Call deposit via writeContract
      useTransactionStore.getState().update(requestId, { step: 'execute', stage: 'wallet', hash: undefined });
      setTradeStep(t('confirming-trade', '確認交易中...'));
      const depositArgs = {
        address: marketAddress as Address,
        abi: predictionAbi,
        functionName: 'deposit' as const,
        args: [buySimParams.optionIndex, buySimParams.tokenAmount, execution.minimum, execution.deadline] as const,
        account,
      };
      const depositGas = await publicClient.estimateContractGas(depositArgs);
      if (!currentRequest(requestId, account.address, accepted.key)) return false;
      if (Number(market.data?.deadline) <= Math.ceil(serverNow())) return false;
      const depositTxHash = await sendTransaction(requestId, () =>
        walletClient.writeContract({
          ...depositArgs,
          chain: walletClient.chain,
          gas: (depositGas * 130n) / 100n,
        })
      );
      const depositReceipt = await watchTransaction(requestId, depositTxHash);
      if (!currentRequest(requestId, account.address) || !consumeTransactionSuccess(requestId)) return false;

      // Optimistic portfolio row: the backend needs ~15s (subgraph indexing)
      // before the position lands in DB; overlay the exact fill from the
      // receipt so the portfolio reflects the buy immediately.
      const fill = extractDepositFill(depositReceipt.logs, marketAddress, account.address);
      if (fill && optionAddress && market.data) {
        wirePendingPositionSse(api.sse, { chainId: APP_CHAIN.id, account: account.address }, uid);
        usePendingPositionStore.getState().addPending({
          owner: { chainId: APP_CHAIN.id, account: account.address },
          marketAddress,
          optionAddress,
          marketName: market.data.marketName,
          nameI18n: market.data.nameI18n,
          marketPicUrl: market.data.marketPicUrl,
          marketDescription: market.data.marketDescription,
          optionName: currentOptionInfo?.optionName ?? '',
          optionDescription: currentOptionInfo?.description ?? '',
          optionPicUrl: currentOptionInfo?.optionPicUrl ?? '',
          optionDecimal: currentOptionInfo?.decimal ?? market.data.decimal,
          baseTokenAddress: baseToken.address,
          eventId: market.data.eventId,
          eventTitle: eventDetail.data?.title,
          eventTitleI18n: eventDetail.data?.titleI18n,
          eventMarketCount: eventDetail.data?.markets?.length,
          deadline: market.data.deadline,
          shares: fill.amountOut.toString(),
          amountIn: fill.amountIn.toString(),
        });
      }

      // Record order on backend — non-fatal, tx already succeeded on-chain
      try {
        await api.market.recordOrder(
          { baseTokenAddress: baseToken.address },
          {
            txHash: useTransactionStore.getState().records[requestId]?.hash ?? depositTxHash,
            marketAddress: marketAddress as Address,
            optionAddress: optionAddress as Address,
            side: OrderSide.deposit,
            amount: buySimParams.tokenAmount.toString(),
            price,
          }
        );
      } catch (recordErr) {
        if (IS_DEBUG) console.warn('recordOrder failed (tx succeeded on-chain):', recordErr);
      }

      if (!currentRequest(requestId, account.address, accepted.key)) return false;
      toast.success(t('buy-success'));
      setAmount(undefined);
      await onSuccess?.();
      return true;
    } catch (error) {
      if (error instanceof Error && error.message === 'transaction-superseded') return false;
      if (IS_DEBUG) console.log('buy error', error);
      if (isInsufficientFundsError(error)) {
        toast.error(t('insufficient-gas', 'Insufficient BNB for gas fees. Please top up your wallet.'));
      } else {
        const msg = getErrorMessage(error);
        toast.error(msg?.startsWith('transaction-') ? t(msg) : (msg ?? t('buy-failed')));
      }
      return false;
    }
  };
  const handleSell = async (requestId: string, accepted: AcceptedQuote) => {
    // In mock mode, use simplified validation and skip contract calls
    if (isMockMode) {
      console.log('[MockTrade] handleSell called', {
        type,
        marketAddress,
        optionAddress,
        amount: amount?.toString(),
        price,
      });
      if (type != 'sell' || !marketAddress || !optionAddress || !sellSimParams) {
        console.log('[MockTrade] validation failed - missing required fields');
        return;
      }
      const tradePrice = price ?? '500000';
      try {
        console.log('[MockTrade] calling placeOrder API...');
        await api.market.placeOrder(
          { baseTokenAddress: baseToken.address },
          {
            marketAddress: marketAddress as Address,
            optionAddress: optionAddress as Address,
            side: OrderSide.withdraw,
            price: tradePrice,
            amount: sellSimParams.optionAmount.toString(),
            minReceiveAmount: accepted.minimum.toString(),
            deadline: transactionDeadline(serverNowMs(), deadlineMinutes).toString(),
            userOperation: {} as any,
          }
        );
        console.log('[MockTrade] placeOrder success!');
        toast.success(t('sell-success'));
        setAmount(undefined);
        await onSuccess?.();
        return true;
      } catch (error) {
        console.log('[MockTrade] placeOrder error:', error);
        const msg = getErrorMessage(error);
        if (IS_DEBUG) console.log('sell error', error);
        toast.error(msg?.startsWith('transaction-') ? t(msg) : (msg ?? t('sell-failed')));
        return false;
      }
    }

    if (type != 'sell' || !market.data || !marketAddress || !sellSimParams || price === undefined) return;
    if (!walletClient?.account) {
      toast.error(t('wallet-not-connected', 'Please connect your wallet'));
      return;
    }
    if (!guardSignerMatchesSession(walletClient.account.address)) return false;
    // Check market deadline before submitting on-chain tx
    if (Number(market.data?.deadline) <= Math.ceil(serverNow())) {
      toast.error(t('market-expired', 'Market has expired'));
      return false;
    }
    const account = walletClient.account;
    try {
      // Step 0: Ensure CTF ERC-1155 approval for the market contract
      setTradeStep(t('checking-approval', '檢查授權...'));
      if (CTF_ADDRESS) {
        const isApproved = await publicClient.readContract({
          address: CTF_ADDRESS,
          abi: [
            {
              type: 'function',
              name: 'isApprovedForAll',
              stateMutability: 'view',
              inputs: [
                { name: 'account', type: 'address' },
                { name: 'operator', type: 'address' },
              ],
              outputs: [{ name: '', type: 'bool' }],
            },
          ] as const,
          functionName: 'isApprovedForAll',
          args: [account.address, marketAddress as Address],
        });
        const approvalConfirmed = useTransactionStore.getState().records[requestId]?.approvalConfirmed;
        if (!isApproved && !approvalConfirmed) {
          useTransactionStore.getState().update(requestId, { step: 'approve' });
          setTradeStep(t('approving-token', '授權代幣中...'));
          const approvalArgs = {
            address: CTF_ADDRESS,
            abi: [
              {
                type: 'function',
                name: 'setApprovalForAll',
                stateMutability: 'nonpayable',
                inputs: [
                  { name: 'operator', type: 'address' },
                  { name: 'approved', type: 'bool' },
                ],
                outputs: [],
              },
            ] as const,
            functionName: 'setApprovalForAll' as const,
            args: [marketAddress as Address, true] as const,
            account,
          };
          const approvalGas = await publicClient.estimateContractGas(approvalArgs);
          if (!currentRequest(requestId, account.address, accepted.key)) return false;
          const approvalTxHash = await sendTransaction(requestId, () =>
            walletClient.writeContract({
              ...approvalArgs,
              chain: walletClient.chain,
              gas: (approvalGas * 130n) / 100n,
            })
          );
          await watchTransaction(requestId, approvalTxHash);
        }
      }

      const execution = await refreshAcceptedQuote(requestId, account.address, accepted);
      if (!execution || !currentRequest(requestId, account.address, accepted.key)) return false;

      // Step 1: Call withdraw via writeContract
      useTransactionStore.getState().update(requestId, { step: 'execute', stage: 'wallet', hash: undefined });
      setTradeStep(t('confirming-trade', '確認交易中...'));
      const withdrawArgs = {
        address: marketAddress as Address,
        abi: predictionAbi,
        functionName: 'withdraw' as const,
        args: [sellSimParams.optionIndex, sellSimParams.optionAmount, execution.minimum, execution.deadline] as const,
        account,
      };
      const withdrawGas = await publicClient.estimateContractGas(withdrawArgs);
      if (!currentRequest(requestId, account.address, accepted.key)) return false;
      if (Number(market.data?.deadline) <= Math.ceil(serverNow())) return false;
      const withdrawTxHash = await sendTransaction(requestId, () =>
        walletClient.writeContract({
          ...withdrawArgs,
          chain: walletClient.chain,
          gas: (withdrawGas * 130n) / 100n,
        })
      );
      const withdrawReceipt = await watchTransaction(requestId, withdrawTxHash);
      if (!currentRequest(requestId, account.address) || !consumeTransactionSuccess(requestId)) return false;

      // Optimistic portfolio update: deduct the exact sold shares from the
      // position row immediately (row hides when fully sold) instead of
      // waiting ~15s for the backend pipeline.
      const sellFill = extractWithdrawFill(withdrawReceipt.logs, marketAddress, account.address);
      if (sellFill && optionAddress) {
        wirePendingPositionSse(api.sse, { chainId: APP_CHAIN.id, account: account.address }, uid);
        usePendingPositionStore.getState().addPendingSell({
          owner: { chainId: APP_CHAIN.id, account: account.address },
          marketAddress,
          optionAddress,
          baseTokenAddress: baseToken.address,
          shares: sellFill.sharesIn.toString(),
        });
      }

      // Record order on backend — non-fatal, tx already succeeded on-chain
      try {
        await api.market.recordOrder(
          { baseTokenAddress: baseToken.address },
          {
            txHash: useTransactionStore.getState().records[requestId]?.hash ?? withdrawTxHash,
            marketAddress: marketAddress as Address,
            optionAddress: optionAddress as Address,
            side: OrderSide.withdraw,
            amount: sellSimParams.optionAmount.toString(),
            price,
          }
        );
      } catch (recordErr) {
        if (IS_DEBUG) console.warn('recordOrder failed (tx succeeded on-chain):', recordErr);
      }

      if (!currentRequest(requestId, account.address, accepted.key)) return false;
      toast.success(t('sell-success'));
      setAmount(undefined);
      await onSuccess?.();
      return true;
    } catch (error) {
      if (error instanceof Error && error.message === 'transaction-superseded') return false;
      if (IS_DEBUG) console.log('sell error', error);
      if (isInsufficientFundsError(error)) {
        toast.error(t('insufficient-gas', 'Insufficient BNB for gas fees. Please top up your wallet.'));
      } else {
        const msg = getErrorMessage(error);
        toast.error(msg?.startsWith('transaction-') ? t(msg) : (msg ?? t('sell-failed')));
      }
      return false;
    }
  };

  const handleClick = async (event: MouseEvent<HTMLButtonElement>) => {
    if (!isAuthed) return;
    const accepted = acceptCurrentQuote();
    if (!accepted || tradingRef.current || !walletClient?.account || walletClient.chain?.id !== APP_CHAIN.id) return;
    const requestId = startTransaction({
      id: crypto.randomUUID(),
      chainId: APP_CHAIN.id,
      account: walletClient.account.address,
      operation: type,
      marketAddress: marketAddress as Address,
      step: 'execute',
      stage: 'wallet',
      startedAt: Date.now(),
    });
    if (!requestId) return;
    tradingRef.current = true;
    const rect = event.currentTarget.getBoundingClientRect();
    setLocalTrading(true);
    setTradeStep('');
    try {
      const success = await (type === 'buy' ? handleBuy(requestId, accepted) : handleSell(requestId, accepted));
      if (success && mounted.current) {
        confetti({
          origin: {
            x: (rect.left + rect.width / 2) / window.innerWidth,
            y: (rect.top + rect.height / 2) / window.innerHeight,
          },
        });
        await (type === 'buy' ? toBuyWin.refetch({ cancelRefetch: false }) : toSellWin.refetch({ cancelRefetch: false }));
      }
    } finally {
      finishTransactionRequest(requestId);
      const record = useTransactionStore.getState().records[requestId];
      if (!record?.approvalConfirmed) failTransactionRequest(requestId);
      tradingRef.current = false;
      if (mounted.current) {
        setLocalTrading(false);
        setTradeStep('');
      }
    }
  };

  return (
    <div className="w-full">
      {tradingUnavailable && (
        <div className="mx-2 mb-2 px-3 py-2 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs text-center">
          {t('trading-temporarily-unavailable', 'Trading temporarily unavailable for this market')}
        </div>
      )}
      {optionAddress && (amount != undefined || isTrading) && !tradingUnavailable && (
        <div className="flex mx-2 text-sm py-2 px-3 justify-center text-foreground/70  items-center gap-1 px-3 py-2 rounded-[16px_16px_0px_0px]">
          <span>
            {type === 'buy' && t('to-win')}
            {type === 'sell' && t('cash-out')}
          </span>
          <CurrencyView
            className="text-beneficial"
            animated={true}
            baseTokenAddress={baseToken.address}
            value={toWin}
            isLoading={toWinLoading}
            decimal={balance.decimal}
            showIcon={true}
          />
        </div>
      )}
      {quoteNeedsRetry && (
        <div className="mx-2 mb-2 flex items-center justify-between gap-2 rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
          <span>{t('quote-unavailable', 'Quote unavailable. Please retry.')}</span>
          <button
            type="button"
            className="shrink-0 underline"
            onClick={() => void activeQuote.refetch({ cancelRefetch: false })}
          >
            {t('retry-quote', 'Retry quote')}
          </button>
        </div>
      )}
      {confirmingChangedQuote && (
        <div
          className="mx-2 mb-2 rounded-lg bg-amber-500/10 px-3 py-2 text-center text-xs text-amber-600 dark:text-amber-400"
          role="alert"
        >
          {t('quote-changed-confirm', 'The quote changed. Review and confirm the new minimum.')}
        </div>
      )}
      <AuthedButton
        loginOptions={{ source: 'trade', tradeParams: { type, marketAddress, optionAddress, optionSide, amount } }}
        className={cn(
          'w-full h-14 hover:text-white text-xl leading-6 font-medium select-none',
          // Gold panel CTA (dpin-redesign-design.md §4/§7: 主按钮金底黑字) — was
          // foreground/background (white/black); dark-only per doc's light-mode
          // scope note, light already resolves bg-primary/text-primary-foreground.
          'dark:text-primary-foreground dark:bg-primary dark:hover:bg-primary/90 dark:hover:text-primary-foreground',
          type == 'buy' && 'hover:bg-beneficial',
          type == 'sell' && 'hover:bg-destructive'
        )}
        isLoading={!isMockMode && signedAllowance.isLoading}
        onKeyDown={handledKeyDownCallBackEnter(handleClick)}
        disabled={
          disabled ||
          tradingUnavailable ||
          toWinLoading ||
          !quoteUsable ||
          !settingsValid ||
          isTrading ||
          !optionAddress ||
          !amount ||
          (isMultiOption && type === 'buy' && !optionSide)
        }
        onClick={handleClick}
        aria-live="polite"
      >
        {isTrading
          ? tradeStep || t('trading')
          : confirmingChangedQuote
            ? t('confirm-new-quote', 'Confirm new quote')
            : type === 'buy'
              ? t('buy')
              : t('sell')}
        {!isTrading && <ApiTranslation>{currentOptionInfo?.description}</ApiTranslation>}
        {!isTrading && isMultiOption && type === 'buy' && optionSide && <span className="uppercase ml-1">{optionSide}</span>}
      </AuthedButton>
      {bscScanUrl && isTrading && (
        <a
          href={bscScanUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block text-center text-xs text-foreground/50 hover:text-foreground/70 mt-1 transition-colors"
        >
          🔗 {tradeTxHash?.slice(0, 6)}...{tradeTxHash?.slice(-4)}
        </a>
      )}
    </div>
  );
};
